import React, { FunctionComponent, useRef, useEffect } from 'react'
import { GlobalEquipmentSearchResult } from '../../../entities/GlobalEquipmentSearchResult'
import EquipmentSearchDropdownOption from '../dropdown-option'
import style from '../equipmentSearchFilter.css'
import useTranslation from '../../../hooks/useTranslation'

export type Props = {
    value: string
    onChange: (value: string) => void
    onSelect: (result: GlobalEquipmentSearchResult) => void
    results: GlobalEquipmentSearchResult[]
    isOpen: boolean
    onToggle: () => void
    placeholder: string
}

const GlobalSearchInput: FunctionComponent<Props> = (props) => {
    const { value, onChange, onSelect, results, isOpen, onToggle, placeholder } = props
    const { t } = useTranslation()
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onToggle()
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen, onToggle])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        onChange(newValue)
    }

    const handleInputFocus = () => {
        if (value.length >= 2 && !isOpen) {
            onToggle()
        }
    }

    const handleResultSelect = (result: GlobalEquipmentSearchResult) => {
        onSelect(result)
        onToggle()
    }

    const handleClear = () => {
        onChange('')
    }

    return (
        <div className={style.customDropdownContainer} ref={dropdownRef}>
            <div className={style.inputContainer}>
                <input
                    id="global-search-input"
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    placeholder={placeholder}
                    className={style.globalSearchInput}
                    data-testid="global-search-input"
                />
                {value && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className={style.clearButton}
                        aria-label="Clear input"
                        data-testid="global-search-clear-button"
                    >
                        <span className={style.clearIcon}>×</span>
                    </button>
                )}
            </div>

            {isOpen && (
                <div className={style.customDropdown} data-testid="global-search-dropdown">
                    {results.length > 0 ? (
                        results.map((result) => (
                            <EquipmentSearchDropdownOption
                                key={result.id}
                                result={result}
                                onSelect={handleResultSelect}
                            />
                        ))
                    ) : (
                        <div className={style.noResultsMessage} data-testid="global-search-no-results">
                            {t('equipmentGlobalSearch.noResultsFound')}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default GlobalSearchInput
